import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ORDER_STAGE } from "@/components/auction/OrderStage";
import { HitSectionLoading, useLoadingLevel } from "@/components/loading";
import { WaButton } from "@/components/WaButton";

const RESULT = { awaiting_payment: "Esperando pago", repechage_offered: "En repechaje", processing_default: "Procesando impago",
  sold: "Vendida", unsold: "Sin venta", no_bids: "Sin pujas" };
const TONE = { amber: "text-amber-300 border-amber-500/40", coral: "text-red-300 border-red-500/40", green: "text-emerald-300 border-emerald-500/40", gray: "text-neutral-400 border-neutral-600" };
const STAGES = [["all", "Todas"], ["PENDIENTE_PAGO", "Esperando pago"], ["PAGADO", "Por enviar"], ["ENVIADO", "Enviadas"],
  ["PENDIENTE_LIBERACION", "Por liberar"], ["LIBERADO", "Liberadas"], ["none", "Sin pedido"]];
const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
const date = (s) => (s ? new Date(s).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" }) : "—");

function Person({ p, label, testid }) {
  if (!p) return <div><b className="text-white">{label}:</b> —</div>;
  return (
    <div data-testid={testid}>
      <b className="text-white">{label}:</b> {p.name || p.email}{p.banned && <span className="ml-1 text-red-400 font-semibold">BANEADO</span>}
      <span className="text-neutral-500"> · {p.email}</span>
      {p.phone ? <> · <a href={`tel:${p.phone}`} className="text-[#EF4444]">{p.phone}</a><WaButton phone={p.phone} testid={`${testid}-whatsapp`} /></>
        : <span className="text-neutral-600"> · sin teléfono</span>}
    </div>
  );
}

function nowStep(a) {
  const of = a.offer;
  if (a.result === "repechage_offered" && of && !of.accepted)
    return `Se le ofreció al postor #${of.rank} a ${money(of.amount)} (su propia puja). Aún no acepta: tiene hasta ${date(of.deadline)} para aceptar y pagar. Si no acepta a tiempo o la rechaza, pasa al siguiente postor sin penalidad. Si acepta y no paga, se le banea y pasa al siguiente.`;
  if ((a.result === "repechage_offered" || a.result === "awaiting_payment") && of)
    return `El postor #${of.rank} aceptó y debe pagar ${money(of.amount)} antes de ${date(of.deadline)}. Si no paga, se le banea y pasa al siguiente postor.`;
  if (a.result === "unsold") return "Ningún postor completó el pago. La subasta cerró sin venta.";
  if (a.result === "sold") return "Pago confirmado. Sigue el flujo normal del pedido (envío y liberación).";
  return null;
}

function Flow({ a }) {
  const step = nowStep(a);
  return (
    <tr className="border-b border-[#1f1f1f] bg-[#0b0b0b]" data-testid={`lifecycle-flow-${a.id}`}>
      <td colSpan={6} className="p-4 text-xs text-neutral-300 space-y-2">
        <Person p={a.seller} label="Vendedor" testid={`lifecycle-seller-${a.id}`} />
        {a.history.map((h) => (
          <div key={h.rank} className="border-l-2 border-red-500/40 pl-2" data-testid={`lifecycle-hist-${a.id}-${h.rank}`}>
            <Person p={h.bidder} label={`Postor #${h.rank}`} testid={`lifecycle-hist-person-${a.id}-${h.rank}`} />
            <div className="text-neutral-500">{money(h.amount)} · plazo {date(h.deadline)} · {h.accepted ? "aceptó y no pagó" : h.declined ? "rechazó la oferta" : "no aceptó a tiempo"}{h.order_status ? ` · pedido ${h.order_status}` : ""}</div>
          </div>
        ))}
        {a.offer && (
          <div className="border-l-2 border-amber-500/60 pl-2">
            <Person p={a.offer.bidder} label={`Postor actual #${a.offer.rank}`} testid={`lifecycle-current-${a.id}`} />
            <div className="text-neutral-500">{money(a.offer.amount)} · plazo {date(a.offer.deadline)} · {a.offer.accepted ? "aceptó" : "aún no acepta"}{!a.order && " · el pedido se crea cuando acepte"}</div>
          </div>
        )}
        {step && <div className="text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded p-2" data-testid={`lifecycle-step-${a.id}`}>{step}</div>}
      </td>
    </tr>
  );
}

function Row({ a }) {
  const o = a.order;
  const [open, setOpen] = useState(false);
  const [label, tone] = o ? ORDER_STAGE[o.status] || [o.status, "gray"] : [RESULT[a.result] || a.result, "gray"];
  return (
    <>
    <tr className="border-b border-[#1f1f1f] align-top" data-testid={`lifecycle-row-${a.id}`}>
      <td className="p-3"><Link to={`/subastas/${a.id}`} className="font-semibold hover:underline">{a.product_name}</Link>
        <div className="text-[11px] text-neutral-500">Cerró {date(a.ends_at)} · {RESULT[a.result] || a.result}</div></td>
      <td className="p-3 text-xs">{a.seller_email || "—"}</td>
      <td className="p-3 text-xs">{a.winner_email || "—"}{a.offer?.rank > 1 && <div className="text-amber-300">Repechaje #{a.offer.rank}{!a.offer.accepted && " · sin aceptar"}</div>}</td>
      <td className="p-3 font-mono text-xs">{money(a.final_price)}{o && <div className="text-neutral-500">Total {money(o.total)}</div>}</td>
      <td className="p-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${TONE[tone]}`} data-testid={`lifecycle-stage-${a.id}`}>{label}</span>
        <div className="text-[11px] text-neutral-500 mt-1">
          {o?.status === "PENDIENTE_PAGO" && `Vence ${date(o.payment_deadline_at)}`}
          {o?.guide_number && `Guía ${o.guide_number}`}
        </div></td>
      <td className="p-3 text-right whitespace-nowrap">
        {o && <Link to={`/order/${o.id}`} className="text-xs text-[#EF4444] hover:underline" data-testid={`lifecycle-order-${a.id}`}>Ver pedido</Link>}
        {o?.status === "PENDIENTE_LIBERACION" && <Link to="/admin/payouts" className="block text-xs text-emerald-300 hover:underline mt-1" data-testid={`lifecycle-release-${a.id}`}>Liberar pago →</Link>}
        <button onClick={() => setOpen(!open)} className="block ml-auto mt-1 text-xs text-amber-300 hover:underline" data-testid={`lifecycle-toggle-${a.id}`}>{open ? "Ocultar flujo" : "Ver flujo"}</button>
      </td>
    </tr>
    {open && <Flow a={a} />}
    </>
  );
}

export function AdminAuctionLifecycle() {
  const [list, setList] = useState(null);
  const [f, setF] = useState("all");
  const level = useLoadingLevel(!list);
  useEffect(() => {
    api.get("/admin/auctions/lifecycle").then((r) => setList(r.data.results || []))
      .catch((e) => { toast.error(e?.response?.data?.detail || "No se pudo cargar el ciclo"); setList([]); });
  }, []);
  if (!list || level !== "none") return <HitSectionLoading level={level} loading={!list} section="admin" />;
  const match = (a, k) => k === "all" || (k === "none" ? !a.order : a.order?.status === k);
  const shown = list.filter((a) => match(a, f));
  return (
    <div className="space-y-3" data-testid="admin-auction-lifecycle">
      <div className="flex gap-1 flex-wrap text-xs">
        {STAGES.map(([k, l]) => (
          <button key={k} onClick={() => setF(k)} data-testid={`lifecycle-filter-${k}`}
                  className={`px-2.5 py-1 rounded border transition-colors ${f === k ? "border-[#EF4444] text-white bg-[#EF4444]/10" : "border-[#262626] text-neutral-400 hover:text-white"}`}>
            {l} ({list.filter((a) => match(a, k)).length})
          </button>
        ))}
      </div>
      <div className="overflow-x-auto bg-[#111] border border-[#262626] rounded-lg">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-[#262626]">
            {["Subasta", "Vendedor", "Ganador", "Precio", "Estado", ""].map((h) => <th key={h} className="p-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>{shown.map((a) => <Row key={a.id} a={a} />)}</tbody>
        </table>
        {!shown.length && <div className="text-neutral-500 text-sm p-6">Sin subastas en este filtro.</div>}
      </div>
    </div>
  );
}
